import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import images from "../../../assets/images";
import { useAuthenticatedFileUrl } from "../../../hooks/useAuthenticatedFileUrl";
import { useUserStore } from "../../../stores/userStore";
import { useAppStore } from "../../../stores/appStore";
import { CallEndIcon, MicOffIcon, MicOnIcon } from "../../../assets/icon";
import { useLiveSpeechSocket } from "../../../utils/liveSpeechSocket";
import { usePushToTalk } from "../../../hooks/usePushToTalk";
import { getPersonaMenu } from "../../../axios/realtimeSpeech";
import {
  cardStyles,
  layoutStyles,
  leftPanelStyles,
  micButtonMutedStyles,
  micButtonStyles,
  getCallMicDimensions,
  getRoleplayCallMicDimensions,
  rootStyles,
  stagePanelStyles,
  topBarStyles,
} from "../styles";
import { RoleplayCallControls } from "../roleplay-call-controls/roleplay-call-controls";
import {
  WaveSurferMicVisualizer,
  WaveSurferAiVisualizer,
} from "../wavesurfer-visualizers/wavesurfer-visualizers";
import { StageTimerPill } from "../stage-timer-pill";
import {
  bottomHalfSx,
  centerControlsSx,
  roleplayCenterControlsSx,
  contextInnerSx,
  doctorAvatarSx,
  doctorNameSx,
  endButtonSx,
  liveDotSx,
  livePillSx,
  liveTextSx,
  mobileCardCompactSx,
  mobileCardsSx,
  mobileStageSx,
  mobileStackSx,
  muteToggleLabelSx,
  sectionBodySx,
  sectionBodyInlineSx,
  sectionTitleSx,
  simulationTitleLabelSx,
  simulationTitleValueSx,
  stageAreaSx,
  stageHeaderSx,
  stageHeaderTitleSx,
  stagePanelContainerSx,
  topBarLeftGroupSx,
  topHalfSx,
  waveLabelBottomSx,
  waveLabelSx,
  waveStretchSx,
} from "./styles";
import { NoiseWarningModal } from "../../noise-warning-modal/noise-warning-modal";
import { LiveSpeechSessionErrorModal } from "../../live-speech-session-error-modal/live-speech-session-error-modal";
import type { LiveSpeechSessionError } from "../../../types/liveSpeechSessionError";
import {
  OFFLINE_SESSION_ERROR,
  isBrowserOffline,
} from "../../../types/liveSpeechSessionError";

interface LiveSpeechStageScreenProps {
  auth: { [key: string]: any };
  additionalMessageProps: { [key: string]: any };
  onConnectionClose: () => void;
}

export type LiveSpeechStageScreenHandle = {
  // Orderly stop-and-flush, then invoke onConnectionClose.
  finish: () => Promise<void>;
};

export const LiveSpeechStageScreen = forwardRef<
  LiveSpeechStageScreenHandle,
  LiveSpeechStageScreenProps
>(function LiveSpeechStageScreen(
  { auth, additionalMessageProps, onConnectionClose },
  ref,
) {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    user: { id: user_id },
    getFocusDisplayName,
  } = useUserStore();

  const {
    focus,
    domain,
    realtimePersona,
    setRealtimePersona,
    lessonSecondsAllowed,
    lessonSecondsRemaining,
    setLessonSecondsRemaining,
    sellingDialogue,
    lessonDialogue,
    incrementLessonTurnsUsed,
    setLessonTurnsUsed,
    realtimeMenuOptions,
    setRealtimeMenuOptions,
    liveSpeechMode,
  } = useAppStore();

  const focusDisplayName = getFocusDisplayName(focus, domain);
  const personaAvatarSrc = realtimePersona?.avatar ?? images.botImage;
  const { url: resolvedPersonaAvatarSrc } =
    useAuthenticatedFileUrl(personaAvatarSrc);

  const [isMuted, setIsMuted] = useState(true);
  const [shouldStopAI, setShouldStopAI] = useState(false);
  const [userActive, setUserActive] = useState(false);
  const [aiActive, setAiActive] = useState(false);
  const [isWarningError, setIsWarningError] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [sessionError, setSessionError] =
    useState<LiveSpeechSessionError | null>(null);

  const isRolePlay = Boolean(sellingDialogue) && !lessonDialogue;

  const simulationTitle = useMemo(() => {
    const focusText =
      typeof focusDisplayName === "string" && focusDisplayName.trim()
        ? focusDisplayName
        : "";
    const domainText =
      typeof domain === "string" && domain.trim() ? domain : "";
    if (focusText && domainText) return `${domainText} • ${focusText}`;
    return focusText || domainText || "Simulation";
  }, [domain, focusDisplayName]);

  useEffect(() => {
    if (sellingDialogue || lessonDialogue) {
      setLessonTurnsUsed(0);
    }
  }, [sellingDialogue, lessonDialogue, setLessonTurnsUsed]);

  const didInitialMuteRef = useRef(false);

  const {
    startRoleplayDialogue,
    resumeSpeeking,
    pauseSpeeking,
    stopSpeeking,
    stopAISpeaking,
    safeExit,
    retrySession,
    clearSessionError,
    playerRef,
    clearNoiseWarning,
    micMediaStream,
    isMicReady,
  } = useLiveSpeechSocket({
    user_id,
    auth,
    additionalMessageProps,
    isMuted,
    setIsMuted,
    onClose: onConnectionClose,
    triggerMicrophoneTip: () => {},
    onTurnCompleted: () => {
      incrementLessonTurnsUsed();
    },
    shouldStopAI,
    onAiPlaybackStateChange: setAiActive,
    onSocketConnected: () => {
      if (isRolePlay && !didInitialMuteRef.current) {
        didInitialMuteRef.current = true;
        setIsMuted(true);
      }
    },
    onSessionError: (error) => {
      setSessionError(error);
    },
    isLessonDialogue: Boolean(lessonDialogue),
    isPushToTalk: isRolePlay,
    isTabVisible: true,
    onUserBargeIn: () => {
      setAiActive(false);
    },
    onUserSpeakingStateChange: (isSpeaking) => {
      setUserActive(isSpeaking);
    },
    onNoiseWarning: (result) => {
      if (result.shouldWarn) {
        setIsWarningError(true);
        setWarningMessage(result.message);
      }
    },
  });

  // Connect socket + mic, wait until connected, then start_ai_dialogue.
  useEffect(() => {
    didInitialMuteRef.current = false;
    void startRoleplayDialogue();
    return () => {
      stopSpeeking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFinishingRef = useRef(false);

  const handleStop = useCallback(async () => {
    if (isFinishingRef.current) return;
    isFinishingRef.current = true;
    try {
      setShouldStopAI(true);
      stopAISpeaking();
      setAiActive(false);
      await safeExit();
    } finally {
      isFinishingRef.current = false;
    }
  }, [stopAISpeaking, safeExit]);

  useImperativeHandle(
    ref,
    () => ({
      finish: handleStop,
    }),
    [handleStop],
  );

  const handleFallbackAudioOnly = useCallback(() => {
    setRealtimeMenuOptions({
      ...realtimeMenuOptions,
      audio_only: true,
    });
    clearSessionError();
    setSessionError(null);
  }, [clearSessionError, realtimeMenuOptions, setRealtimeMenuOptions]);

  const handleSessionRetry = useCallback(async () => {
    if (isBrowserOffline()) {
      clearSessionError();
      setSessionError({
        ...OFFLINE_SESSION_ERROR,
        canFallbackAudioOnly: false,
      });
      return;
    }

    setShouldStopAI(false);
    clearSessionError();
    setSessionError(null);
    await retrySession();
  }, [clearSessionError, retrySession]);

  const handleSessionExit = useCallback(async () => {
    setSessionError(null);
    await handleStop();
  }, [handleStop]);

  const handleToggleMic = useCallback(async () => {
    if (isMuted) {
      await resumeSpeeking({ allowDuringAi: true, skipBargeInCooldown: true });
    } else {
      await pauseSpeeking();
    }
  }, [isMuted, resumeSpeeking, pauseSpeeking]);

  const handlePushStart = useCallback(() => {
    if (!isMicReady) return;
    void resumeSpeeking({ allowDuringAi: true, skipBargeInCooldown: true });
  }, [isMicReady, resumeSpeeking]);

  const handlePushEnd = useCallback(() => {
    void pauseSpeeking();
  }, [pauseSpeeking]);

  const micNotReady = !isMicReady;

  const {
    buttonRef: pushButtonRef,
    isPressed: isPushPressed,
    pushToTalkHandlers,
  } = usePushToTalk({
    enabled: isRolePlay,
    disabled: micNotReady,
    onPressStart: handlePushStart,
    onPressEnd: handlePushEnd,
  });

  const {
    buttonRef: pushButtonRefMobile,
    isPressed: isPushPressedMobile,
    pushToTalkHandlers: pushToTalkHandlersMobile,
  } = usePushToTalk({
    enabled: isRolePlay,
    disabled: micNotReady,
    onPressStart: handlePushStart,
    onPressEnd: handlePushEnd,
  });

  // Countdown timer tick copied from existing logic
  const secondsRemainingRef = useRef<number | null>(null);
  useEffect(() => {
    if (!lessonSecondsAllowed || !lessonSecondsRemaining) return;
    if (lessonSecondsRemaining <= 0) return;
    secondsRemainingRef.current = lessonSecondsRemaining;

    const interval = window.setInterval(() => {
      if (
        secondsRemainingRef.current === null ||
        secondsRemainingRef.current <= 1
      ) {
        handleStop();
        setLessonSecondsRemaining(0);
      } else {
        secondsRemainingRef.current -= 1;
        setLessonSecondsRemaining(secondsRemainingRef.current);
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [
    lessonSecondsAllowed,
    lessonSecondsRemaining,
    handleStop,
    setLessonSecondsRemaining,
  ]);

  const isCoachingModelAudioOnly =
    liveSpeechMode === "coach_me" &&
    Boolean((realtimeMenuOptions as any)?.audio_only);

  const isAudioOnly =
    Boolean((realtimeMenuOptions as any)?.audio_only) &&
    !isCoachingModelAudioOnly;

  const doctorName = isCoachingModelAudioOnly
    ? "Coach"
    : (realtimePersona?.text ?? "Doctor");

  useEffect(() => {
    let cancelled = false;

    const normalizeAvatarFromPersona = (p: any): string | null => {
      if (!p || typeof p !== "object") return null;
      const v =
        p.avatar ||
        p.avatar_url ||
        p.avatarUrl ||
        p.image ||
        p.image_url ||
        p.imageUrl ||
        p.photo ||
        p.photo_url ||
        p.photoUrl;
      return typeof v === "string" && v.trim() ? v : null;
    };

    const ensureAvatar = async () => {
      if (!realtimePersona || typeof realtimePersona !== "object") return;

      if (
        typeof (realtimePersona as any).avatar === "string" &&
        (realtimePersona as any).avatar.trim()
      ) {
        return;
      }

      const existing = normalizeAvatarFromPersona(realtimePersona);
      if (existing) {
        setRealtimePersona({
          ...(realtimePersona as any),
          avatar: existing,
        } as any);
        return;
      }

      try {
        const menu = await getPersonaMenu();
        const options: any[] =
          (menu && (menu as any).options && Array.isArray((menu as any).options)
            ? (menu as any).options
            : Array.isArray(menu)
              ? menu
              : Array.isArray((menu as any)?.[0]?.options)
                ? (menu as any)[0].options
                : []) || [];

        const match = options.find(
          (opt) =>
            opt && typeof opt === "object" && opt.text === realtimePersona.text,
        );
        const avatar = normalizeAvatarFromPersona(match);
        if (!cancelled && match && avatar) {
          setRealtimePersona({ ...match, avatar } as any);
        }
      } catch {
        // ignore: menu fetch may fail, keep fallback robot avatar
      }
    };

    ensureAvatar();
    return () => {
      cancelled = true;
    };
  }, [realtimePersona, setRealtimePersona]);

  useEffect(() => {
    if (realtimePersona?.avatar) {
      const img = new Image();
      img.src = realtimePersona.avatar;
      img.onload = () => {};
    }
  }, [realtimePersona?.avatar]);

  const centerClearHalfPx = isSmDown ? 66 : isMdDown ? 90 : 110;

  const stageAreaRef = useRef<HTMLDivElement | null>(null);
  const [stageAreaHeight, setStageAreaHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = stageAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const h = Math.floor(el.getBoundingClientRect().height || 0);
      setStageAreaHeight(h > 0 ? h : null);
    });
    ro.observe(el);
    const h = Math.floor(el.getBoundingClientRect().height || 0);
    setStageAreaHeight(h > 0 ? h : null);
    return () => ro.disconnect();
  }, []);

  const waveformHeight = useMemo(() => {
    const maxWave = isSmDown ? 110 : isMdDown ? 96 : 160;
    if (!stageAreaHeight) return maxWave;
    const approxLabelAndGap = 34; // label + spacing
    const usable = stageAreaHeight / 2 - centerClearHalfPx - approxLabelAndGap;
    return Math.max(72, Math.min(maxWave, Math.floor(usable)));
  }, [isSmDown, isMdDown, stageAreaHeight, centerClearHalfPx]);
  const waveformRadius = isMdDown ? 14 : 18;
  const { buttonPx: micBtnPx, iconPx: micIconPx } = getCallMicDimensions(
    isSmDown,
    isMdDown,
  );
  const { buttonPx: roleplayBtnPx, iconPx: roleplayIconPx } =
    getRoleplayCallMicDimensions(isSmDown, isMdDown);

  const mobileWaveHeight = useMemo(() => {
    if (!isMdDown) return waveformHeight;
    return 50;
  }, [isMdDown, waveformHeight]);

  const mobileRoleplayBtnPx = roleplayBtnPx;
  const mobileRoleplayIconPx = roleplayIconPx;
  const compactLandscapeButtonSx = {
    "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
      {
        width: 28,
        height: 28,
      },
  };

  return (
    <Box sx={rootStyles}>
      <Box sx={layoutStyles}>
        <Box sx={topBarStyles}>
          <Box sx={topBarLeftGroupSx}>
            <Typography sx={simulationTitleLabelSx}>
              SIMULATION TITLE:
            </Typography>
            <Typography sx={simulationTitleValueSx} title={simulationTitle}>
              {simulationTitle}
            </Typography>
            <Box sx={livePillSx}>
              <Box sx={liveDotSx} />
              <Typography sx={liveTextSx}>LIVE</Typography>
            </Box>
          </Box>

          {!isCoachingModelAudioOnly && (
            <StageTimerPill
              remainingSeconds={
                lessonSecondsAllowed != null && lessonSecondsRemaining != null
                  ? lessonSecondsRemaining
                  : null
              }
            />
          )}
        </Box>

        {isMdDown ? (
          // Mobile/Tablet: all blocks in a single column; THE STAGE is last and fills remaining height.
          <Box sx={mobileStackSx}>
            <Box sx={mobileCardsSx}>
              <Box
                sx={(theme) => ({
                  ...cardStyles(theme),
                  ...mobileCardCompactSx,
                })}
              >
                <Typography sx={sectionTitleSx}>CONTEXT</Typography>
                <Box sx={contextInnerSx}>
                  {isCoachingModelAudioOnly ? (
                    <Box
                      component="img"
                      src={images.CoachingModel}
                      alt="Coaching avatar"
                      loading="eager"
                      sx={doctorAvatarSx}
                    />
                  ) : isAudioOnly ? (
                    <Box
                      component="img"
                      src={images.AudioOnlyAvatar}
                      alt="Audio only avatar"
                      loading="eager"
                      sx={doctorAvatarSx}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={resolvedPersonaAvatarSrc || personaAvatarSrc}
                      alt="Doctor avatar"
                      loading="eager"
                      sx={doctorAvatarSx}
                    />
                  )}
                  <Typography sx={doctorNameSx}>{doctorName}</Typography>
                  {/* <Typography sx={doctorLevelSx}>
                    {realtimeMaturityLevel || "HCP"}
                  </Typography> */}
                </Box>
              </Box>

              <Box
                sx={(theme) => ({
                  ...cardStyles(theme),
                  ...mobileCardCompactSx,
                  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                    {
                      flex: "1 1 auto",
                    },
                })}
              >
                <Typography sx={sectionTitleSx}>ENVIRONMENT STATUS</Typography>
                <Typography sx={sectionBodyInlineSx}>
                  <strong>Domain:</strong> {domain || "—"} •{" "}
                  <strong>Focus:</strong> {focusDisplayName || "—"}
                </Typography>
              </Box>

              <Box
                sx={(theme) => ({
                  ...cardStyles(theme),
                  ...mobileCardCompactSx,
                  "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                    {
                      flex: "1 1 auto",
                    },
                })}
              >
                <Typography sx={sectionTitleSx}>MISSION OBJECTIVE</Typography>
                <Typography sx={sectionBodySx}>
                  Talk to the {isCoachingModelAudioOnly ? "Coach" : "HCP"},
                  listen to objections, and respond clearly.
                </Typography>
              </Box>
            </Box>

            <Box sx={mobileStageSx}>
              <Box
                sx={(theme) => ({
                  ...stagePanelStyles(theme),
                  ...stagePanelContainerSx,
                  height: "100%",
                })}
              >
                <Box sx={stageHeaderSx}>
                  <Typography sx={stageHeaderTitleSx}>THE STAGE</Typography>
                </Box>

                {/* Mobile/Tablet: stacked stage layout (no absolute overlay) to guarantee it fits */}
                <Box
                  ref={stageAreaRef}
                  sx={{
                    ...(stageAreaSx as any),
                    display: "flex",
                    flexDirection: "column",
                    gap: {
                      xs: 0.4,
                      sm: 0.5,
                      md: 0.5,
                      "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                        "2px",
                    },
                  }}
                >
                  <Typography sx={waveLabelSx}>
                    AI ({isCoachingModelAudioOnly ? "Coach" : "HCP"}){" "}
                    {aiActive ? "SPEAKING" : ""}
                  </Typography>
                  <Box
                    sx={{
                      "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                        {
                          maxHeight: 30,
                          overflow: "hidden",
                        },
                    }}
                  >
                    <WaveSurferAiVisualizer
                      playerRef={playerRef}
                      isActive={aiActive}
                      height={mobileWaveHeight}
                      borderRadiusPx={14}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: { xs: 0.5, sm: 0.75, md: 0.75 },
                      py: { xs: 0.15, sm: 0.25, md: 0.25 },
                      width: isRolePlay ? "100%" : undefined,
                      px: isRolePlay ? { xs: 0.5, sm: 1 } : undefined,
                      "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                        {
                          gap: "2px",
                          py: 0.1,
                        },
                    }}
                  >
                    {isRolePlay ? (
                      <RoleplayCallControls
                        buttonPx={mobileRoleplayBtnPx}
                        iconPx={mobileRoleplayIconPx}
                        isPushPressed={isPushPressedMobile}
                        onEndCall={handleStop}
                        mediaStream={micMediaStream}
                        pushButtonRef={pushButtonRefMobile}
                        pushToTalkHandlers={pushToTalkHandlersMobile}
                        buttonSx={compactLandscapeButtonSx}
                        endButtonSx={compactLandscapeButtonSx}
                        isDisabled={micNotReady}
                      />
                    ) : (
                      <>
                        <IconButton
                          onClick={handleToggleMic}
                          sx={{
                            ...(isMuted
                              ? micButtonMutedStyles
                              : micButtonStyles),
                            width: micBtnPx,
                            height: micBtnPx,
                            "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                              {
                                width: 32,
                                height: 32,
                              },
                          }}
                          title={isMuted ? "Unmute" : "Mute"}
                        >
                          {isMuted ? (
                            <Box
                              sx={{
                                width: micIconPx,
                                height: micIconPx,
                                "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                                  {
                                    width: 16,
                                    height: 16,
                                  },
                              }}
                            >
                              <MicOffIcon
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  color: "currentColor",
                                }}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: micIconPx,
                                height: micIconPx,
                                "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                                  {
                                    width: 16,
                                    height: 16,
                                  },
                              }}
                            >
                              <MicOnIcon
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  color: "currentColor",
                                }}
                              />
                            </Box>
                          )}
                        </IconButton>
                        <Typography sx={muteToggleLabelSx}>
                          MUTE / UNMUTE TOGGLE
                        </Typography>
                        <IconButton
                          onClick={handleStop}
                          sx={{
                            ...(endButtonSx as any),
                            mt: { xs: 0.25, sm: 0.5 },
                            width: micBtnPx,
                            height: micBtnPx,
                            "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                              {
                                width: 32,
                                height: 32,
                                mt: 0.125,
                              },
                          }}
                          title="End"
                        >
                          <Box
                            sx={{
                              width: Math.round(micIconPx * 0.65),
                              height: Math.round(micIconPx * 0.65),
                              "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                                {
                                  width: 12,
                                  height: 12,
                                },
                            }}
                          >
                            <CallEndIcon
                              style={{
                                width: "100%",
                                height: "100%",
                                color: "#FFB3AA",
                              }}
                            />
                          </Box>
                        </IconButton>
                      </>
                    )}
                  </Box>

                  <Box
                    sx={{
                      "@media (orientation: landscape) and (max-height: 500px) and (pointer: coarse)":
                        {
                          maxHeight: 30,
                          overflow: "hidden",
                        },
                    }}
                  >
                    <WaveSurferMicVisualizer
                      isRecording={!isMuted}
                      height={mobileWaveHeight}
                      borderRadiusPx={14}
                    />
                  </Box>
                  <Typography sx={waveLabelBottomSx}>
                    USER (TAP) {userActive ? "SPEAKING" : ""}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={leftPanelStyles}>
              <Box
                sx={(theme) => ({
                  ...cardStyles(theme),
                  // Desktop: CONTEXT takes half of the left column height (gap-aware)
                  flex: "1 1 0%",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                })}
              >
                <Typography sx={sectionTitleSx}>CONTEXT</Typography>
                <Box sx={contextInnerSx}>
                  {isCoachingModelAudioOnly ? (
                    <Box
                      component="img"
                      src={images.CoachingModel}
                      alt="Coaching avatar"
                      loading="eager"
                      sx={doctorAvatarSx}
                    />
                  ) : isAudioOnly ? (
                    <Box
                      component="img"
                      src={images.AudioOnlyAvatar}
                      alt="Audio only avatar"
                      loading="eager"
                      sx={doctorAvatarSx}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={resolvedPersonaAvatarSrc || personaAvatarSrc}
                      alt="Doctor avatar"
                      loading="eager"
                      sx={doctorAvatarSx}
                    />
                  )}
                  <Typography sx={doctorNameSx}>{doctorName}</Typography>
                  {/* <Typography sx={doctorLevelSx}>
                    {realtimeMaturityLevel || "HCP"}
                  </Typography> */}
                </Box>
              </Box>

              {/* Desktop: bottom half is split equally between the two cards (gap-aware) */}
              <Box
                sx={{
                  flex: "1 1 0%",
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box
                  sx={(theme) => ({
                    ...cardStyles(theme),
                    flex: "1 1 0%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  })}
                >
                  <Typography sx={sectionTitleSx}>
                    ENVIRONMENT STATUS
                  </Typography>
                  <Typography sx={sectionBodyInlineSx}>
                    <strong>Domain:</strong> {domain || "—"} •{" "}
                    <strong>Focus:</strong> {focusDisplayName || "—"}
                  </Typography>
                </Box>

                <Box
                  sx={(theme) => ({
                    ...cardStyles(theme),
                    flex: "1 1 0%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  })}
                >
                  <Typography sx={sectionTitleSx}>MISSION OBJECTIVE</Typography>
                  <Typography sx={sectionBodySx}>
                    Talk to the {isCoachingModelAudioOnly ? "Coach" : "HCP"},
                    listen to objections, and respond clearly.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={(theme) => ({
                ...stagePanelStyles(theme),
                ...stagePanelContainerSx,
              })}
            >
              <Box sx={stageHeaderSx}>
                <Typography sx={stageHeaderTitleSx}>THE STAGE</Typography>
              </Box>

              <Box ref={stageAreaRef} sx={stageAreaSx}>
                <Box sx={topHalfSx(centerClearHalfPx)}>
                  <Typography sx={waveLabelSx}>
                    AI ({isCoachingModelAudioOnly ? "Coach" : "HCP"}){" "}
                    {aiActive ? "SPEAKING" : ""}
                  </Typography>
                  <Box sx={waveStretchSx}>
                    <WaveSurferAiVisualizer
                      playerRef={playerRef}
                      isActive={aiActive}
                      height={waveformHeight}
                      borderRadiusPx={waveformRadius}
                    />
                  </Box>
                </Box>

                <Box sx={bottomHalfSx(centerClearHalfPx)}>
                  <Box sx={waveStretchSx}>
                    <WaveSurferMicVisualizer
                      isRecording={!isMuted}
                      height={waveformHeight}
                      borderRadiusPx={waveformRadius}
                    />
                  </Box>
                  <Typography sx={waveLabelBottomSx}>
                    USER (TAP) {userActive ? "SPEAKING" : ""}
                  </Typography>
                </Box>

                <Box
                  sx={isRolePlay ? roleplayCenterControlsSx : centerControlsSx}
                >
                  {isRolePlay ? (
                    <RoleplayCallControls
                      buttonPx={roleplayBtnPx}
                      iconPx={roleplayIconPx}
                      isPushPressed={isPushPressed}
                      onEndCall={handleStop}
                      mediaStream={micMediaStream}
                      pushButtonRef={pushButtonRef}
                      pushToTalkHandlers={pushToTalkHandlers}
                      isDisabled={micNotReady}
                    />
                  ) : (
                    <>
                      <IconButton
                        onClick={handleToggleMic}
                        sx={{
                          ...(isMuted ? micButtonMutedStyles : micButtonStyles),
                          width: micBtnPx,
                          height: micBtnPx,
                        }}
                        title={isMuted ? "Unmute" : "Mute"}
                      >
                        {isMuted ? (
                          <MicOffIcon
                            style={{
                              width: micIconPx,
                              height: micIconPx,
                              color: "currentColor",
                            }}
                          />
                        ) : (
                          <MicOnIcon
                            style={{
                              width: micIconPx,
                              height: micIconPx,
                              color: "currentColor",
                            }}
                          />
                        )}
                      </IconButton>
                      <Typography sx={muteToggleLabelSx}>
                        MUTE / UNMUTE TOGGLE
                      </Typography>
                      <IconButton
                        onClick={handleStop}
                        sx={{
                          ...endButtonSx,
                          width: micBtnPx,
                          height: micBtnPx,
                        }}
                        title="End"
                      >
                        <CallEndIcon
                          style={{
                            width: Math.round(micIconPx * 0.65),
                            height: Math.round(micIconPx * 0.65),
                            color: "#FFB3AA",
                          }}
                        />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Noise warning overlay */}
      <NoiseWarningModal
        open={!!(warningMessage && isWarningError)}
        message={warningMessage}
        toast={isRolePlay}
        autoDismissMs={3000}
        onContinue={() => {
          setWarningMessage(null);
          setIsWarningError(false);
          clearNoiseWarning();
        }}
        onClose={() => {
          setWarningMessage(null);
          setIsWarningError(false);
          clearNoiseWarning();
          void handleSessionExit();
        }}
      />

      <LiveSpeechSessionErrorModal
        error={sessionError}
        onRetry={
          sessionError?.retryable ? () => void handleSessionRetry() : undefined
        }
        onFallbackAudioOnly={
          sessionError?.canFallbackAudioOnly
            ? handleFallbackAudioOnly
            : undefined
        }
        onExit={() => void handleSessionExit()}
      />
    </Box>
  );
});
